import { ApolloQueryResult } from 'apollo-client';
import { Href } from 'redux-little-router';

import { InterestsSetType } from '../components/InterestsSetEditor/InterestsSetEditor';
import { ApiMethodExecutingError } from '../service/Errors';
import {
    EditGroupFieldErrorsData,
    EditGroupValidationParams
} from '../service/GroupEditErrors/ClientGroupEditErrors/ClientGroupEditErrors';
import {
    ErrorsEdit,
    PageStatus,
    Region,
    ValidationErrorData
} from '../typings/common';
import { DisplayListItem, SelectedScreens } from '../typings/displayMap';
import { CampaignId } from '../typings/domain';
import { GroupsQueryForEdit } from '../typings/graphql-grid';
import { GroupId, TypeModeGroup } from '../typings/groups';
import {
    CpmGroupShowConditionType,
    EditGroupFilterItem,
    ProductsList,
    RetargetingValue,
    UpdateDataForEditGroups,
    UpdateRetargetingConditionsForGroupsEdit
} from '../typings/groupsEditState';
import { AudienceCollection } from '../typings/groupsEditState';
import { MinusPhrasesLibraryItem } from '../typings/minusPhraseLibrary';
import { MinusKeywordsEditorData } from '../typings/minusPhrases';
import {
    RateCorrection,
    RateCorrectionType
} from '../typings/ratesCorrections';
import { RetargetingConditionData } from '../typings/retargeting';

export const GET_GROUPS_FOR_EDIT = 'GET_GROUPS_FOR_EDIT';
export type GET_GROUPS_FOR_EDIT = typeof GET_GROUPS_FOR_EDIT;

export interface GetGroupsForEditAction {
    type: GET_GROUPS_FOR_EDIT;
    login: string;
}

export function getGroupsForEditAction(login: string): GetGroupsForEditAction {
    return {
        type: GET_GROUPS_FOR_EDIT,
        login
    };
}

export const RESET_GROUPS_EDIT = 'RESET_GROUPS_EDIT';
export type RESET_GROUPS_EDIT = typeof RESET_GROUPS_EDIT;

export interface ResetGroupsEdit {
    type: RESET_GROUPS_EDIT;
}

export function resetGroupsEdit(): ResetGroupsEdit {
    return {
        type: RESET_GROUPS_EDIT
    };
}

export const GET_CPC_GROUPS_FOR_EDIT = 'GET_CPC_GROUPS_FOR_EDIT';
export type GET_CPC_GROUPS_FOR_EDIT = typeof GET_CPC_GROUPS_FOR_EDIT;

export interface GetCpcGroupsForEditAction {
    type: GET_CPC_GROUPS_FOR_EDIT;
    login: string;
    groupsData: ApolloQueryResult<GroupsQueryForEdit>;
}

export function getCpcGroupsForEditAction(
    login: string,
    groupsData: ApolloQueryResult<GroupsQueryForEdit>
): GetCpcGroupsForEditAction {
    return {
        type: GET_CPC_GROUPS_FOR_EDIT,
        login,
        groupsData
    };
}

export const GET_INTERNAL_GROUPS_FOR_EDIT = 'GET_INTERNAL_GROUPS_FOR_EDIT';
export type GET_INTERNAL_GROUPS_FOR_EDIT = typeof GET_INTERNAL_GROUPS_FOR_EDIT;

export interface GetInternalGroupsForEditAction {
    type: GET_INTERNAL_GROUPS_FOR_EDIT;
    login: string;
    groupsData: ApolloQueryResult<GroupsQueryForEdit>;
}

export function getInternalGroupsForEditAction(
    login: string,
    groupsData: ApolloQueryResult<GroupsQueryForEdit>
): GetInternalGroupsForEditAction {
    return {
        type: GET_INTERNAL_GROUPS_FOR_EDIT,
        login,
        groupsData
    };
}

export const GET_CPM_GROUPS_FOR_EDIT = 'GET_CPM_GROUPS_FOR_EDIT';
export type GET_CPM_GROUPS_FOR_EDIT = typeof GET_CPM_GROUPS_FOR_EDIT;

export interface GetCpmGroupsForEditAction {
    type: GET_CPM_GROUPS_FOR_EDIT;
    login: string;
    groupsData: ApolloQueryResult<GroupsQueryForEdit>;
}

export function getCpmGroupsForEditAction(
    login: string,
    groupsData: ApolloQueryResult<GroupsQueryForEdit>
): GetCpmGroupsForEditAction {
    return {
        type: GET_CPM_GROUPS_FOR_EDIT,
        login,
        groupsData
    };
}

export const UPDATE_PRODUCT_RESTRICTION_ID = 'UPDATE_PRODUCT_RESTRICTION_ID';
export type UPDATE_PRODUCT_RESTRICTION_ID = typeof UPDATE_PRODUCT_RESTRICTION_ID;

export interface UpdateProductRestrictionIdAction {
    type: UPDATE_PRODUCT_RESTRICTION_ID;
    groupId: GroupId;
    id: string;
}

export function updateProductRestrictionIdAction(
    groupId: GroupId,
    id: string
): UpdateProductRestrictionIdAction {
    return {
        type: UPDATE_PRODUCT_RESTRICTION_ID,
        groupId,
        id
    };
}

export const UPDATE_GROUP_TYPE = 'UPDATE_GROUP_TYPE';
export type UPDATE_GROUP_TYPE = typeof UPDATE_GROUP_TYPE;

export interface UpdateGroupTypeAction {
    type: UPDATE_GROUP_TYPE;
    groupId: GroupId;
    group: TypeModeGroup;
}

export function updateGroupType(
    groupId: GroupId,
    group: TypeModeGroup
): UpdateGroupTypeAction {
    return {
        type: UPDATE_GROUP_TYPE,
        groupId,
        group
    };
}

export const UPDATE_SHOW_CONDITION_TYPE = 'UPDATE_SHOW_CONDITION_TYPE';
export type UPDATE_SHOW_CONDITION_TYPE = typeof UPDATE_SHOW_CONDITION_TYPE;

export interface UpdateShowConditionTypeAction {
    type: UPDATE_SHOW_CONDITION_TYPE;
    groupId: GroupId;
    ulogin: string;
    showConditionType: CpmGroupShowConditionType;
}

export function updateShowConditionTypeAction(
    groupId: GroupId,
    showConditionType: CpmGroupShowConditionType,
    ulogin: string
): UpdateShowConditionTypeAction {
    return {
        type: UPDATE_SHOW_CONDITION_TYPE,
        groupId,
        ulogin,
        showConditionType
    };
}

export const GET_RETARGETING_CONDITIONS_FOR_GROUPS_EDIT =
    'GET_RETARGETING_CONDITIONS_FOR_GROUPS_EDIT';
export type GET_RETARGETING_CONDITIONS_FOR_GROUPS_EDIT = typeof GET_RETARGETING_CONDITIONS_FOR_GROUPS_EDIT;

export interface GetRetargetingConditionsForGroupsEditAction {
    type: GET_RETARGETING_CONDITIONS_FOR_GROUPS_EDIT;
    login: string;
}

export function getRetargetingConditionsForGroupsEdit(
    login: string
): GetRetargetingConditionsForGroupsEditAction {
    return {
        type: GET_RETARGETING_CONDITIONS_FOR_GROUPS_EDIT,
        login
    };
}

export const GET_CAMPAIGN_BASIC_DATA = 'GET_CAMPAIGN_BASIC_DATA';
export type GET_CAMPAIGN_BASIC_DATA = typeof GET_CAMPAIGN_BASIC_DATA;

export interface GetCampaignBasicDataAction {
    type: GET_CAMPAIGN_BASIC_DATA;
    login: string;
    campaignId: string;
}

export function getCampaignBasicData(
    login: string,
    campaignId: string
): GetCampaignBasicDataAction {
    return {
        type: GET_CAMPAIGN_BASIC_DATA,
        login,
        campaignId
    };
}

export const SAVE_GROUPS = 'SAVE_GROUPS';
export type SAVE_GROUPS = typeof SAVE_GROUPS;

export interface SaveGroupsAction {
    type: SAVE_GROUPS;
    withRedirect: boolean;
}

export function saveGroups(withRedirect?: boolean): SaveGroupsAction {
    return {
        type: SAVE_GROUPS,
        withRedirect: withRedirect === undefined ? true : withRedirect
    };
}

export const UPDATE_DATA_FOR_EDIT_GROUPS = 'UPDATE_DATA_FOR_EDIT_GROUPS';
export type UPDATE_DATA_FOR_EDIT_GROUPS = typeof UPDATE_DATA_FOR_EDIT_GROUPS;

export interface UpdateDataForEditGroupsAction {
    type: UPDATE_DATA_FOR_EDIT_GROUPS;
    data: UpdateDataForEditGroups;
    afterSave: boolean;
}

export function updateDataForEditGroups(
    data: UpdateDataForEditGroups,
    afterSave: boolean = false
): UpdateDataForEditGroupsAction {
    return {
        type: UPDATE_DATA_FOR_EDIT_GROUPS,
        data: data,
        // Для того чтобы можно было определить, вызывался
        // этот action после сохранения или при открытии редактирования
        afterSave
    };
}

export const UPDATE_RETARGETING_CONDITIONS_FOR_EDIT_GROUPS =
    'UPDATE_RETARGETING_CONDITIONS_FOR_EDIT_GROUPS';
export type UPDATE_RETARGETING_CONDITIONS_FOR_EDIT_GROUPS = typeof UPDATE_RETARGETING_CONDITIONS_FOR_EDIT_GROUPS;

export interface UpdateRetargetingConditionsForEditGroupsAction {
    type: UPDATE_RETARGETING_CONDITIONS_FOR_EDIT_GROUPS;
    data: UpdateRetargetingConditionsForGroupsEdit;
}

export function updateRetargetingConditionsForEditGroups(
    data: UpdateRetargetingConditionsForGroupsEdit
): UpdateRetargetingConditionsForEditGroupsAction {
    return {
        type: UPDATE_RETARGETING_CONDITIONS_FOR_EDIT_GROUPS,
        data: data
    };
}

export const CHANGE_GROUP_EDIT_PAGE_STATUS = 'CHANGE_GROUP_EDIT_PAGE_STATUS';
export type CHANGE_GROUP_EDIT_PAGE_STATUS = typeof CHANGE_GROUP_EDIT_PAGE_STATUS;

export interface ChangeGroupEditPageStatusAction {
    type: CHANGE_GROUP_EDIT_PAGE_STATUS;
    pageStatus: PageStatus;
}

export function changeGroupEditPageStatus(
    pageStatus: PageStatus
): ChangeGroupEditPageStatusAction {
    return {
        type: CHANGE_GROUP_EDIT_PAGE_STATUS,
        pageStatus
    };
}

export const SAVE_GROUP_NAME_EDITOR_CHANGES = 'SAVE_GROUP_NAME_EDITOR_CHANGES';
export type SAVE_GROUP_NAME_EDITOR_CHANGES = typeof SAVE_GROUP_NAME_EDITOR_CHANGES;

export interface SaveGroupNameEditorChangesAction {
    type: SAVE_GROUP_NAME_EDITOR_CHANGES;
    groupId: GroupId;
}

export function saveGroupNameEditorChanges(
    groupId: GroupId
): SaveGroupNameEditorChangesAction {
    return {
        type: SAVE_GROUP_NAME_EDITOR_CHANGES,
        groupId
    };
}

export const CHANGE_GROUP_NAME = 'CHANGE_GROUP_NAME';
export type CHANGE_GROUP_NAME = typeof CHANGE_GROUP_NAME;

export interface ChangeGroupName {
    type: CHANGE_GROUP_NAME;
    groupId: GroupId;
    name: string;
}

export function changeGroupName(
    groupId: GroupId,
    name: string
): ChangeGroupName {
    return {
        type: CHANGE_GROUP_NAME,
        groupId,
        name
    };
}

export const CHANGE_GROUP_HREF_PARAMS = 'CHANGE_GROUP_HREF_PARAMS';
export type CHANGE_GROUP_HREF_PARAMS = typeof CHANGE_GROUP_HREF_PARAMS;

export interface ChangeGroupHrefParamsAction {
    type: CHANGE_GROUP_HREF_PARAMS;
    groupId: GroupId;
    params: string;
}

export function changeGroupHrefParams(
    groupId: GroupId,
    params: string
): ChangeGroupHrefParamsAction {
    return {
        type: CHANGE_GROUP_HREF_PARAMS,
        groupId,
        params
    };
}

export const CHANGE_GROUP_BANNER_HEADERS_FEED_FIELD =
    'CHANGE_GROUP_BANNER_HEADERS_FEED_FIELD';
export type CHANGE_GROUP_BANNER_HEADERS_FEED_FIELD = typeof CHANGE_GROUP_BANNER_HEADERS_FEED_FIELD;

export interface ChangeGroupBannerHeadersFeedFieldAction {
    type: CHANGE_GROUP_BANNER_HEADERS_FEED_FIELD;
    groupId: GroupId;
    payload: string;
}

export function changeGroupBannerHeadersFeedField(
    groupId: GroupId,
    payload: string
): ChangeGroupBannerHeadersFeedFieldAction {
    return {
        type: CHANGE_GROUP_BANNER_HEADERS_FEED_FIELD,
        groupId,
        payload
    };
}

export const CHANGE_GROUP_BANNER_TEXT_FEED_FIELD =
    'CHANGE_GROUP_BANNER_TEXT_FEED_FIELD';
export type CHANGE_GROUP_BANNER_TEXT_FEED_FIELD = typeof CHANGE_GROUP_BANNER_TEXT_FEED_FIELD;

export interface ChangeGroupBannerTextFeedFieldAction {
    type: CHANGE_GROUP_BANNER_TEXT_FEED_FIELD;
    groupId: GroupId;
    payload: string;
}

export function changeGroupBannerTextFeedField(
    groupId: GroupId,
    payload: string
): ChangeGroupBannerTextFeedFieldAction {
    return {
        type: CHANGE_GROUP_BANNER_TEXT_FEED_FIELD,
        groupId,
        payload
    };
}

export const AUDIENCE_TARGETING_TEMPLATE_NAME_CHANGE =
    'AUDIENCE_TARGETING_TEMPLATE_NAME_CHANGE';
export type AUDIENCE_TARGETING_TEMPLATE_NAME_CHANGE = typeof AUDIENCE_TARGETING_TEMPLATE_NAME_CHANGE;

export interface AudienceTargetingTemplateNameChange {
    type: AUDIENCE_TARGETING_TEMPLATE_NAME_CHANGE;
    groupId: string;
    value: string;
}

export function audienceTargetingTemplateNameChange(
    groupId: string,
    value: string
): AudienceTargetingTemplateNameChange {
    return {
        type: AUDIENCE_TARGETING_TEMPLATE_NAME_CHANGE,
        groupId,
        value
    };
}

export const CHANGE_AUDIENCE_VALUE = 'CHANGE_AUDIENCE_VALUE';
export type CHANGE_AUDIENCE_VALUE = typeof CHANGE_AUDIENCE_VALUE;

export interface ChangeAudienceValueAction {
    type: CHANGE_AUDIENCE_VALUE;
    groupId: GroupId;
    value: AudienceCollection[];
}
export function changeAudienceValue(
    groupId: GroupId,
    value: AudienceCollection[]
): ChangeAudienceValueAction {
    return {
        type: CHANGE_AUDIENCE_VALUE,
        groupId,
        value
    };
}

export const CLEAR_GROUP_AUDIENCE_RETARGETING_CONDITION =
    'CLEAR_GROUP_AUDIENCE_RETARGETING_CONDITION';
export type CLEAR_GROUP_AUDIENCE_RETARGETING_CONDITION = typeof CLEAR_GROUP_AUDIENCE_RETARGETING_CONDITION;

export interface ClearGroupAudienceRetargetingCondition {
    type: CLEAR_GROUP_AUDIENCE_RETARGETING_CONDITION;
    groupId: string;
}

export function clearGroupAudienceRetargetingCondition(
    groupId: string
): ClearGroupAudienceRetargetingCondition {
    return {
        type: CLEAR_GROUP_AUDIENCE_RETARGETING_CONDITION,
        groupId
    };
}

export const CHANGE_GENERAL_RATE = 'CHANGE_GENERAL_RATE';
export type CHANGE_GENERAL_RATE = typeof CHANGE_GENERAL_RATE;

export interface ChangeGeneralRate {
    type: CHANGE_GENERAL_RATE;
    groupId: GroupId;
    generalRate?: number;
    validationParams: EditGroupValidationParams;
}

export function changeGeneralRate(
    groupId: GroupId,
    validationParams: EditGroupValidationParams,
    generalRate?: number
): ChangeGeneralRate {
    return {
        type: CHANGE_GENERAL_RATE,
        groupId,
        generalRate,
        validationParams
    };
}

export const SHOW_GROUP_EDIT_ERROR = 'SHOW_GROUP_EDIT_ERROR';
export type SHOW_GROUP_EDIT_ERROR = typeof SHOW_GROUP_EDIT_ERROR;

export interface ShowGroupEditErrorAction {
    type: SHOW_GROUP_EDIT_ERROR;
    error: ValidationErrorData;
}

export function showGroupEditError(
    error: ApiMethodExecutingError | Error | string
): ShowGroupEditErrorAction {
    let message: string;

    if (error instanceof ApiMethodExecutingError) {
        message = error.getMessage();
    } else if (error instanceof Error) {
        message = error.message;
    } else {
        message = error;
    }

    return {
        type: SHOW_GROUP_EDIT_ERROR,
        error: {
            code: 'showGroupEditError',
            text: message
        }
    };
}

export const SHOW_GROUPS_CLIENT_VALIDATION_ERRORS =
    'SHOW_GROUPS_CLIENT_VALIDATION_ERRORS';
export type SHOW_GROUPS_CLIENT_VALIDATION_ERRORS = typeof SHOW_GROUPS_CLIENT_VALIDATION_ERRORS;

export interface ShowGroupsClientValidationErrors {
    type: SHOW_GROUPS_CLIENT_VALIDATION_ERRORS;
    errorsData: Record<GroupId, Record<string, EditGroupFieldErrorsData>>;
}

export function showGroupsClientValidationErrors(
    errorsData: Record<GroupId, Record<string, EditGroupFieldErrorsData>>
): ShowGroupsClientValidationErrors {
    return {
        type: SHOW_GROUPS_CLIENT_VALIDATION_ERRORS,
        errorsData
    };
}

export const UPDATE_GROUP_MINUS_PHRASES = 'UPDATE_GROUP_MINUS_PHRASES';
export type UPDATE_GROUP_MINUS_PHRASES = typeof UPDATE_GROUP_MINUS_PHRASES;

export interface UpdateGroupMinusPhrases {
    type: UPDATE_GROUP_MINUS_PHRASES;
    groupId: GroupId;
    minusKeywordsEditor: MinusKeywordsEditorData;
    libraryItems: MinusPhrasesLibraryItem[];
}

export function updateGroupMinusPhrases(
    groupId: GroupId,
    minusKeywordsEditor: MinusKeywordsEditorData,
    libraryItems: MinusPhrasesLibraryItem[]
): UpdateGroupMinusPhrases {
    return {
        type: UPDATE_GROUP_MINUS_PHRASES,
        groupId,
        minusKeywordsEditor,
        libraryItems
    };
}

export const SAVE_GROUP_MINUS_PHRASES_EDITOR =
    'SAVE_GROUP_MINUS_PHRASES_EDITOR';
export type SAVE_GROUP_MINUS_PHRASES_EDITOR = typeof SAVE_GROUP_MINUS_PHRASES_EDITOR;

export interface SaveGroupMinusPhrasesEditorAction {
    type: SAVE_GROUP_MINUS_PHRASES_EDITOR;
    groupId: GroupId;
}

export function saveGroupMinusPhrasesEditor(
    groupId: GroupId
): SaveGroupMinusPhrasesEditorAction {
    return {
        type: SAVE_GROUP_MINUS_PHRASES_EDITOR,
        groupId
    };
}

export const INPUT_CHANGE_GROUP_EDITOR_MINUS_KEYWORDS =
    'INPUT_CHANGE_GROUP_EDITOR_MINUS_KEYWORDS';
export type INPUT_CHANGE_GROUP_EDITOR_MINUS_KEYWORDS = typeof INPUT_CHANGE_GROUP_EDITOR_MINUS_KEYWORDS;

export interface InputChangeGroupEditorMinusKeywords {
    type: INPUT_CHANGE_GROUP_EDITOR_MINUS_KEYWORDS;
    groupId: GroupId;
}

export function inputChangeGroupEditorMinusKeywords(
    groupId: GroupId
): InputChangeGroupEditorMinusKeywords {
    return {
        type: INPUT_CHANGE_GROUP_EDITOR_MINUS_KEYWORDS,
        groupId
    };
}

export const CHANGE_GROUP_EDITOR_KEYWORDS = 'CHANGE_GROUP_EDITOR_KEYWORDS';
export type CHANGE_GROUP_EDITOR_KEYWORDS = typeof CHANGE_GROUP_EDITOR_KEYWORDS;

export interface ChangeGroupEditorKeywords {
    type: CHANGE_GROUP_EDITOR_KEYWORDS;
    groupId: GroupId;
}

export function changeGroupEditorKeywords(
    groupId: GroupId
): ChangeGroupEditorKeywords {
    return {
        type: CHANGE_GROUP_EDITOR_KEYWORDS,
        groupId
    };
}

export const DONE_GROUP_EDITOR_KEYWORDS = 'DONE_GROUP_EDITOR_KEYWORDS';
export type DONE_GROUP_EDITOR_KEYWORDS = typeof DONE_GROUP_EDITOR_KEYWORDS;

export interface DoneGroupEditorKeywords {
    type: DONE_GROUP_EDITOR_KEYWORDS;
    groupId: GroupId;
}

export function doneGroupEditorKeywords(
    groupId: GroupId
): DoneGroupEditorKeywords {
    return {
        type: DONE_GROUP_EDITOR_KEYWORDS,
        groupId
    };
}

export const OPEN_EDITOR_KEYWORDS = 'OPEN_EDITOR_KEYWORDS';
export type OPEN_EDITOR_KEYWORDS = typeof OPEN_EDITOR_KEYWORDS;

export interface OpenGroupEditorKeywords {
    type: OPEN_EDITOR_KEYWORDS;
    groupId: GroupId;
}

export function openGroupEditorKeywords(
    groupId: GroupId
): OpenGroupEditorKeywords {
    return {
        type: OPEN_EDITOR_KEYWORDS,
        groupId
    };
}

export const OPEN_PHRASES_SELECTOR_WIZARD_FOR_GROUPS_EDIT =
    'OPEN_PHRASES_SELECTOR_WIZARD_FOR_GROUPS_EDIT';
export type OPEN_PHRASES_SELECTOR_WIZARD_FOR_GROUPS_EDIT = typeof OPEN_PHRASES_SELECTOR_WIZARD_FOR_GROUPS_EDIT;

export interface OpenPhrasesSelectorWizardForGroupsEdit {
    type: OPEN_PHRASES_SELECTOR_WIZARD_FOR_GROUPS_EDIT;
    groupId: GroupId;
}

export function openPhrasesSelectorWizardForGroupsEdit(
    groupId: GroupId
): OpenPhrasesSelectorWizardForGroupsEdit {
    return {
        type: OPEN_PHRASES_SELECTOR_WIZARD_FOR_GROUPS_EDIT,
        groupId
    };
}

export const SHOW_GROUPS_PARTIAL_SAVE_RESULT =
    'SHOW_GROUPS_PARTIAL_SAVE_RESULT';
export type SHOW_GROUPS_PARTIAL_SAVE_RESULT = typeof SHOW_GROUPS_PARTIAL_SAVE_RESULT;

export interface ShowGroupsPartialSaveResultPayload {
    updatedGroupIds: Array<GroupId>;
    errors: ErrorsEdit;
}

export interface ShowGroupsPartialSaveResultAction {
    type: SHOW_GROUPS_PARTIAL_SAVE_RESULT;
    payload: ShowGroupsPartialSaveResultPayload;
}

export function showGroupsPartialSaveResult(
    payload: ShowGroupsPartialSaveResultPayload
): ShowGroupsPartialSaveResultAction {
    return {
        type: SHOW_GROUPS_PARTIAL_SAVE_RESULT,
        payload
    };
}

export const CHANGE_GROUP_SAVED_STATUS = 'CHANGE_GROUP_SAVED_STATUS';
export type CHANGE_GROUP_SAVED_STATUS = typeof CHANGE_GROUP_SAVED_STATUS;

export interface ChangeGroupSavedStatus {
    type: CHANGE_GROUP_SAVED_STATUS;
    groupId: GroupId;
}

export function changeGroupSavedStatus(
    groupId: GroupId
): ChangeGroupSavedStatus {
    return {
        type: CHANGE_GROUP_SAVED_STATUS,
        groupId
    };
}

export const LEAVE_GROUPS_EDIT = 'LEAVE_GROUPS_EDIT';
export type LEAVE_GROUPS_EDIT = typeof LEAVE_GROUPS_EDIT;

export interface LeaveGroupsEdit {
    type: LEAVE_GROUPS_EDIT;
    location: Href;
    showConfirm?: boolean;
}

export function leaveGroupsEdit(
    location: Href,
    showConfirm: boolean
): LeaveGroupsEdit {
    return {
        type: LEAVE_GROUPS_EDIT,
        location,
        showConfirm
    };
}

export const CHANGE_INPUT_GROUP_EDITOR_KEYWORDS =
    'CHANGE_INPUT_GROUP_EDITOR_KEYWORDS';
export type CHANGE_INPUT_GROUP_EDITOR_KEYWORDS = typeof CHANGE_INPUT_GROUP_EDITOR_KEYWORDS;

export interface ChangeInputGroupEditorKeywords {
    type: CHANGE_INPUT_GROUP_EDITOR_KEYWORDS;
    groupId: GroupId;
    value: string;
}

export function changeInputGroupEditorKeywords(
    groupId: GroupId,
    value: string
): ChangeInputGroupEditorKeywords {
    return {
        type: CHANGE_INPUT_GROUP_EDITOR_KEYWORDS,
        groupId,
        value
    };
}

export const CANCEL_GROUP_EDITOR_KEYWORDS = 'CANCEL_GROUP_EDITOR_KEYWORDS';
export type CANCEL_GROUP_EDITOR_KEYWORDS = typeof CANCEL_GROUP_EDITOR_KEYWORDS;

export interface CancelGroupEditorKeywords {
    type: CANCEL_GROUP_EDITOR_KEYWORDS;
    groupId: GroupId;
}

export function cancelGroupEditorKeywords(
    groupId: GroupId
): CancelGroupEditorKeywords {
    return {
        type: CANCEL_GROUP_EDITOR_KEYWORDS,
        groupId
    };
}

export const APPLY_SELECTOR_WIZARD_FOR_GROUP_EDIT =
    'APPLY_SELECTOR_WIZARD_FOR_GROUP_EDIT';
export type APPLY_SELECTOR_WIZARD_FOR_GROUP_EDIT = typeof APPLY_SELECTOR_WIZARD_FOR_GROUP_EDIT;

export interface ApplySelectorWizardForGroupEdit {
    type: APPLY_SELECTOR_WIZARD_FOR_GROUP_EDIT;
    groupId: string;
    listChosenKeywords: string[];
    minusPhrases: string[];
}

export function applySelectorWizardForGroupEdit(
    groupId: string,
    listChosenKeywords: string[],
    minusPhrases: string[]
): ApplySelectorWizardForGroupEdit {
    return {
        type: APPLY_SELECTOR_WIZARD_FOR_GROUP_EDIT,
        groupId,
        listChosenKeywords,
        minusPhrases
    };
}

export const UPDATE_GROUP_RATE_CORRECTIONS = 'UPDATE_GROUP_RATE_CORRECTIONS';
export type UPDATE_GROUP_RATE_CORRECTIONS = typeof UPDATE_GROUP_RATE_CORRECTIONS;

export interface UpdateGroupRateCorrectionsAction {
    type: UPDATE_GROUP_RATE_CORRECTIONS;
    groupId: GroupId;
    rateCorrections: Record<RateCorrectionType, RateCorrection>;
}

export function updateGroupRateCorrections(
    groupId: GroupId,
    rateCorrections: Record<RateCorrectionType, RateCorrection>
): UpdateGroupRateCorrectionsAction {
    return {
        type: UPDATE_GROUP_RATE_CORRECTIONS,
        groupId: groupId,
        rateCorrections: rateCorrections
    };
}

export const UPDATE_GROUP_AUDIENCE_TARGETING_INTERESTS =
    'UPDATE_GROUP_AUDIENCE_TARGETING_INTERESTS';
export type UPDATE_GROUP_AUDIENCE_TARGETING_INTERESTS = typeof UPDATE_GROUP_AUDIENCE_TARGETING_INTERESTS;

export interface UpdateGroupAudienceTargetingInterests {
    type: UPDATE_GROUP_AUDIENCE_TARGETING_INTERESTS;
    id: string;
    interests: InterestsSetType[];
}

export function updateGroupAudienceTargetingInterests(
    id: string,
    interests: InterestsSetType[]
): UpdateGroupAudienceTargetingInterests {
    return {
        type: UPDATE_GROUP_AUDIENCE_TARGETING_INTERESTS,
        id,
        interests
    };
}

export const UPDATE_GROUP_AUDIENCE_RETARGETING_CONDITION =
    'UPDATE_GROUP_AUDIENCE_RETARGETING_CONDITION';
export type UPDATE_GROUP_AUDIENCE_RETARGETING_CONDITION = typeof UPDATE_GROUP_AUDIENCE_RETARGETING_CONDITION;

export interface UpdateGroupAudienceRetargetingCondition {
    type: UPDATE_GROUP_AUDIENCE_RETARGETING_CONDITION;
    id: string;
    conditionData: RetargetingConditionData;
}

export function updateGroupAudienceRetargetingCondition(
    id: string,
    conditionData: RetargetingConditionData
): UpdateGroupAudienceRetargetingCondition {
    return {
        type: UPDATE_GROUP_AUDIENCE_RETARGETING_CONDITION,
        id,
        conditionData
    };
}

export const UPDATE_GROUP_AUDIENCE_CRYPTA_SOCIAL_DEMO =
    'UPDATE_GROUP_AUDIENCE_CRYPTA_SOCIAL_DEMO';
export type UPDATE_GROUP_AUDIENCE_CRYPTA_SOCIAL_DEMO = typeof UPDATE_GROUP_AUDIENCE_CRYPTA_SOCIAL_DEMO;

export interface UpdateGroupAudienceCryptaSocialDemo {
    type: UPDATE_GROUP_AUDIENCE_CRYPTA_SOCIAL_DEMO;
    id: string;
    socialDemo: string[];
}

export function updateGroupAudienceCryptaSocialDemo(
    id: string,
    socialDemo: string[]
): UpdateGroupAudienceCryptaSocialDemo {
    return {
        type: UPDATE_GROUP_AUDIENCE_CRYPTA_SOCIAL_DEMO,
        id,
        socialDemo
    };
}

export const UPDATE_GROUP_OUTDOOR_MAP = 'UPDATE_GROUP_OUTDOOR_MAP';
export type UPDATE_GROUP_OUTDOOR_MAP = typeof UPDATE_GROUP_OUTDOOR_MAP;

export interface UpdateGroupOutdoorMap {
    type: UPDATE_GROUP_OUTDOOR_MAP;
    id: string;
    checkedByRegion: SelectedScreens;
}

export function updateGroupOutdoorMap(
    id: string,
    checkedByRegion: SelectedScreens
): UpdateGroupOutdoorMap {
    return {
        type: UPDATE_GROUP_OUTDOOR_MAP,
        id,
        checkedByRegion
    };
}

export const UPDATE_GROUP_INDOOR_MAP = 'UPDATE_GROUP_INDOOR_MAP';
export type UPDATE_GROUP_INDOOR_MAP = typeof UPDATE_GROUP_INDOOR_MAP;

export interface UpdateGroupIndoorMap {
    type: UPDATE_GROUP_INDOOR_MAP;
    id: string;
    checkedByRegion: SelectedScreens;
    screenGroupsByRegion: {
        [id: string]: DisplayListItem[];
    };
}

export function updateGroupIndoorMap(
    id: string,
    checkedByRegion: SelectedScreens,
    screenGroupsByRegion: {
        [id: string]: DisplayListItem[];
    }
): UpdateGroupIndoorMap {
    return {
        type: UPDATE_GROUP_INDOOR_MAP,
        id,
        checkedByRegion,
        screenGroupsByRegion
    };
}

export const UPDATE_GROUP_AUDIENCE_CRYPTA_GROUP_FAMILY =
    'UPDATE_GROUP_AUDIENCE_CRYPTA_GROUP_FAMILY';
export type UPDATE_GROUP_AUDIENCE_CRYPTA_GROUP_FAMILY = typeof UPDATE_GROUP_AUDIENCE_CRYPTA_GROUP_FAMILY;

export interface UpdateGroupAudienceCryptaGroupFamily {
    type: UPDATE_GROUP_AUDIENCE_CRYPTA_GROUP_FAMILY;
    id: string;
    groupFamily: string[];
}

export function updateGroupAudienceCryptaGroupFamily(
    id: string,
    groupFamily: string[]
): UpdateGroupAudienceCryptaGroupFamily {
    return {
        type: UPDATE_GROUP_AUDIENCE_CRYPTA_GROUP_FAMILY,
        id,
        groupFamily
    };
}

export const UPDATE_GROUP_AUDIENCE_CRYPTA_AUDIO_GENRES =
    'UPDATE_GROUP_AUDIENCE_CRYPTA_AUDIO_GENRES';
export type UPDATE_GROUP_AUDIENCE_CRYPTA_AUDIO_GENRES = typeof UPDATE_GROUP_AUDIENCE_CRYPTA_AUDIO_GENRES;

export interface UpdateGroupAudienceCryptaAudioGenres {
    type: UPDATE_GROUP_AUDIENCE_CRYPTA_AUDIO_GENRES;
    id: string;
    audioGenres: string[];
}

export function updateGroupAudienceCryptaAudioGenres(
    id: string,
    audioGenres: string[]
): UpdateGroupAudienceCryptaAudioGenres {
    return {
        type: UPDATE_GROUP_AUDIENCE_CRYPTA_AUDIO_GENRES,
        id,
        audioGenres
    };
}

export const UPDATE_GROUP_AUDIENCE_CRYPTA_BEHAVIORAL =
    'UPDATE_GROUP_AUDIENCE_CRYPTA_BEHAVIORAL';
export type UPDATE_GROUP_AUDIENCE_CRYPTA_BEHAVIORAL = typeof UPDATE_GROUP_AUDIENCE_CRYPTA_BEHAVIORAL;

export interface UpdateGroupAudienceCryptaBehavioral {
    type: UPDATE_GROUP_AUDIENCE_CRYPTA_BEHAVIORAL;
    id: string;
    behavioral: string[];
}

export function updateGroupAudienceCryptaBehavioral(
    id: string,
    behavioral: string[]
): UpdateGroupAudienceCryptaBehavioral {
    return {
        type: UPDATE_GROUP_AUDIENCE_CRYPTA_BEHAVIORAL,
        id,
        behavioral
    };
}

export const TOGGLE_GROUP_AUTOTARGETING = 'TOGGLE_GROUP_AUTOTARGETING';
export type TOGGLE_GROUP_AUTOTARGETING = typeof TOGGLE_GROUP_AUTOTARGETING;

export interface ToggleGroupAutotargeting {
    type: TOGGLE_GROUP_AUTOTARGETING;
    groupId: GroupId;
}

export function toggleGroupAutotargeting(
    groupId: GroupId
): ToggleGroupAutotargeting {
    return {
        type: TOGGLE_GROUP_AUTOTARGETING,
        groupId
    };
}

export const OPEN_GROUP_FILTERS_LIST_EDITOR = 'OPEN_GROUP_FILTERS_LIST_EDITOR';
export type OPEN_GROUP_FILTERS_LIST_EDITOR = typeof OPEN_GROUP_FILTERS_LIST_EDITOR;

export interface OpenGroupFiltersListEditorAction {
    type: OPEN_GROUP_FILTERS_LIST_EDITOR;
    groupId: GroupId;
}

export function openGroupFiltersListEditor(
    groupId: GroupId
): OpenGroupFiltersListEditorAction {
    return {
        type: OPEN_GROUP_FILTERS_LIST_EDITOR,
        groupId
    };
}

export const OPEN_GROUP_RETARGETING_EDITOR = 'OPEN_GROUP_RETARGETING_EDITOR';
export type OPEN_GROUP_RETARGETING_EDITOR = typeof OPEN_GROUP_RETARGETING_EDITOR;

export interface OpenGroupRetargetingEditor {
    type: OPEN_GROUP_RETARGETING_EDITOR;
    groupId: GroupId;
}

export function openGroupRetargetingEditor(
    groupId: string
): OpenGroupRetargetingEditor {
    return {
        type: OPEN_GROUP_RETARGETING_EDITOR,
        groupId
    };
}

export const CLOSE_GROUP_FILTERS_LIST_EDITOR =
    'CLOSE_GROUP_FILTERS_LIST_EDITOR';
export type CLOSE_GROUP_FILTERS_LIST_EDITOR = typeof CLOSE_GROUP_FILTERS_LIST_EDITOR;

export interface CloseGroupFiltersListEditorAction {
    type: CLOSE_GROUP_FILTERS_LIST_EDITOR;
    groupId: GroupId;
}

export function closeGroupFiltersListEditor(
    groupId: GroupId
): CloseGroupFiltersListEditorAction {
    return {
        type: CLOSE_GROUP_FILTERS_LIST_EDITOR,
        groupId
    };
}

export const SAVE_GROUP_RETARGETING_EDITOR = 'SAVE_GROUP__RETARGETING_EDITOR';
export type SAVE_GROUP_RETARGETING_EDITOR = typeof SAVE_GROUP_RETARGETING_EDITOR;

export interface SaveGroupRetargetingEditor {
    type: SAVE_GROUP_RETARGETING_EDITOR;
    groupId: string;
}

export function saveGroupRetargetingEditor(
    groupId: string
): SaveGroupRetargetingEditor {
    return {
        type: SAVE_GROUP_RETARGETING_EDITOR,
        groupId
    };
}

export const SET_GROUP_FILTERS = 'SET_GROUP_FILTERS';
export type SET_GROUP_FILTERS = typeof SET_GROUP_FILTERS;

export interface SetGroupFiltersAction {
    type: SET_GROUP_FILTERS;
    filters: Array<EditGroupFilterItem>;
    groupId: GroupId;
}

export function setGroupFilters(
    groupId: GroupId,
    filters: Array<EditGroupFilterItem>
): SetGroupFiltersAction {
    return {
        type: SET_GROUP_FILTERS,
        filters,
        groupId
    };
}

export const DELETE_GROUP_FILTER = 'DELETE_GROUP_FILTER';
export type DELETE_GROUP_FILTER = typeof DELETE_GROUP_FILTER;

export interface DeleteGroupFilterAction {
    type: DELETE_GROUP_FILTER;
    groupId: GroupId;
    filterId: string;
}

export function deleteGroupFilter(
    groupId: GroupId,
    filterId: string
): DeleteGroupFilterAction {
    return {
        type: DELETE_GROUP_FILTER,
        groupId,
        filterId
    };
}

export const GET_GROUP_FILTERS = 'GET_GROUP_FILTERS';
export type GET_GROUP_FILTERS = typeof GET_GROUP_FILTERS;

export interface GetGroupFiltersAction {
    type: GET_GROUP_FILTERS;
    groupId: string;
}

export function getGroupFilters(groupId: string): GetGroupFiltersAction {
    return {
        type: GET_GROUP_FILTERS,
        groupId
    };
}

export const CHANGE_GROUP_RETARGETING = 'CHANGE_GROUP_RETARGETING';
export type CHANGE_GROUP_RETARGETING = typeof CHANGE_GROUP_RETARGETING;

export interface ChangeGroupRetargeting {
    type: CHANGE_GROUP_RETARGETING;
    groupId: string;
    value: Array<RetargetingValue>;
}

export function changeGroupRetargeting(
    groupId: string,
    value: Array<RetargetingValue>
): ChangeGroupRetargeting {
    return {
        type: CHANGE_GROUP_RETARGETING,
        groupId,
        value
    };
}

export const CANCEL_GROUP_EDITOR_REGIONS = 'CANCEL_GROUP_EDITOR_REGIONS';
export type CANCEL_GROUP_EDITOR_REGIONS = typeof CANCEL_GROUP_EDITOR_REGIONS;

export interface CancelGroupEditorRegions {
    type: CANCEL_GROUP_EDITOR_REGIONS;
    groupId: GroupId;
    selectedMap: Record<number, boolean>;
}

export function cancelGroupEditorRegions(
    groupId: GroupId,
    selectedMap: Record<number, boolean>
): CancelGroupEditorRegions {
    return {
        type: CANCEL_GROUP_EDITOR_REGIONS,
        groupId,
        selectedMap
    };
}

export const SHOW_GROUP_EDITOR_REGIONS_ERRORS =
    'SHOW_GROUP_EDITOR_REGIONS_ERRORS';
export type SHOW_GROUP_EDITOR_REGIONS_ERRORS = typeof SHOW_GROUP_EDITOR_REGIONS_ERRORS;

export interface ShowGroupEditorRegionsErrors {
    type: SHOW_GROUP_EDITOR_REGIONS_ERRORS;
    groupId: GroupId;
    errors: Array<ValidationErrorData>;
}

export function showGroupEditorRegionsErrors(
    groupId: GroupId,
    errors: Array<ValidationErrorData>
): ShowGroupEditorRegionsErrors {
    return {
        type: SHOW_GROUP_EDITOR_REGIONS_ERRORS,
        groupId,
        errors
    };
}

export const SAVE_GROUP_EDITOR_REGIONS = 'SAVE_GROUP_EDITOR_REGIONS';
export type SAVE_GROUP_EDITOR_REGIONS = typeof SAVE_GROUP_EDITOR_REGIONS;

export interface SaveGroupEditorRegions {
    type: SAVE_GROUP_EDITOR_REGIONS;
    groupId: GroupId;
    selectedMap: Record<number, boolean>;
    tree: Region[];
}

export function saveGroupEditorRegions(
    groupId: GroupId,
    selectedMap: Record<number, boolean>,
    tree: Region[]
): SaveGroupEditorRegions {
    return {
        type: SAVE_GROUP_EDITOR_REGIONS,
        groupId,
        selectedMap,
        tree
    };
}

export const UPDATE_GROUP_EDITOR_REGIONS = 'UPDATE_GROUP_EDITOR_REGIONS';
export type UPDATE_GROUP_EDITOR_REGIONS = typeof UPDATE_GROUP_EDITOR_REGIONS;

export interface UpdateGroupEditorRegions {
    type: UPDATE_GROUP_EDITOR_REGIONS;
    groupId: GroupId;
    selectedMap: Record<number, boolean>;
    tree: Region[];
}

export function updateGroupEditorRegions(
    groupId: GroupId,
    selectedMap: Record<number, boolean>,
    tree: Region[]
): UpdateGroupEditorRegions {
    return {
        type: UPDATE_GROUP_EDITOR_REGIONS,
        groupId,
        selectedMap,
        tree
    };
}

export const CLEAR_ERRORS_GROUP_EDITOR_REGIONS =
    'CLEAR_ERRORS_GROUP_EDITOR_REGIONS';
export type CLEAR_ERRORS_GROUP_EDITOR_REGIONS = typeof CLEAR_ERRORS_GROUP_EDITOR_REGIONS;

export interface ClearErrorsGroupEditorRegions {
    type: CLEAR_ERRORS_GROUP_EDITOR_REGIONS;
    groupId: GroupId;
}

export function clearErrorsGroupEditorRegions(
    groupId: GroupId
): ClearErrorsGroupEditorRegions {
    return {
        type: CLEAR_ERRORS_GROUP_EDITOR_REGIONS,
        groupId
    };
}

export const SAVE_GROUP_FILTERS = 'SAVE_GROUP_FILTERS';
export type SAVE_GROUP_FILTERS = typeof SAVE_GROUP_FILTERS;

export interface SaveGroupFiltersAction {
    type: SAVE_GROUP_FILTERS;
    groupId: GroupId;
}

export function saveGroupFilters(groupId: GroupId): SaveGroupFiltersAction {
    return {
        type: SAVE_GROUP_FILTERS,
        groupId
    };
}

export const UPDATE_FILTERS_EDITOR_AUXILIARY_FILTER =
    'UPDATE_FILTERS_EDITOR_AUXILIARY_FILTER';
export type UPDATE_FILTERS_EDITOR_AUXILIARY_FILTER = typeof UPDATE_FILTERS_EDITOR_AUXILIARY_FILTER;

export interface UpdateFiltersEditorAuxiliaryFilterAction {
    type: UPDATE_FILTERS_EDITOR_AUXILIARY_FILTER;
    groupId: GroupId;
    value: string;
}

export function updateFiltersEditorAuxiliaryFilter(
    groupId: GroupId,
    value: string
): UpdateFiltersEditorAuxiliaryFilterAction {
    return {
        type: UPDATE_FILTERS_EDITOR_AUXILIARY_FILTER,
        groupId,
        value
    };
}

export const CREATE_TEXT_AD_GROUP = 'CREATE_TEXT_AD_GROUP';
export type CREATE_TEXT_AD_GROUP = typeof CREATE_TEXT_AD_GROUP;

export interface CreateTextAdGroupAction {
    type: CREATE_TEXT_AD_GROUP;
    campaignId: CampaignId;
    name: string;
    replaceLocation?: boolean;
}

export function createTextAdGroup(
    campaignId: CampaignId,
    name: string,
    replaceLocation?: boolean
): CreateTextAdGroupAction {
    return {
        type: CREATE_TEXT_AD_GROUP,
        campaignId,
        name,
        replaceLocation
    };
}

export const CREATE_SMART_AD_GROUP = 'CREATE_SMART_AD_GROUP';
export type CREATE_SMART_AD_GROUP = typeof CREATE_SMART_AD_GROUP;

export interface CreateSmartAdGroupAction {
    type: CREATE_SMART_AD_GROUP;
    campaignId: CampaignId;
    feedId: string;
    name: string;
    mockGroupId: GroupId;
}

export function createSmartAdGroup(
    campaignId: CampaignId,
    feedId: string,
    name: string,
    mockGroupId: GroupId
): CreateSmartAdGroupAction {
    return {
        type: CREATE_SMART_AD_GROUP,
        campaignId,
        feedId,
        name,
        mockGroupId
    };
}

export const MOVE_NEW_GROUP_TO_CREATED = 'MOVE_NEW_GROUP_TO_CREATED';
export type MOVE_NEW_GROUP_TO_CREATED = typeof MOVE_NEW_GROUP_TO_CREATED;

export interface MoveNewGroupToCreatedAction {
    type: MOVE_NEW_GROUP_TO_CREATED;
    mockGroupId: GroupId;
    realGroupId: GroupId;
    feedId: string;
}

export function moveNewGroupToCreated(
    mockGroupId: GroupId,
    realGroupId: GroupId,
    feedId: string
): MoveNewGroupToCreatedAction {
    return {
        type: MOVE_NEW_GROUP_TO_CREATED,
        mockGroupId,
        realGroupId,
        feedId
    };
}

export const DELETE_RETARGETING_CONDITION_FROM_GROUP =
    'DELETE_RETARGETING_CONDITION_FROM_GROUP';
export type DELETE_RETARGETING_CONDITION_FROM_GROUP = typeof DELETE_RETARGETING_CONDITION_FROM_GROUP;

export interface DeleteRetargetingConditionFromGroup {
    type: DELETE_RETARGETING_CONDITION_FROM_GROUP;
    id: string;
    index: number;
}

export function deleteRetargetingConditionFromGroup(
    id: string,
    index: number
): DeleteRetargetingConditionFromGroup {
    return {
        type: DELETE_RETARGETING_CONDITION_FROM_GROUP,
        id,
        index
    };
}

export const UPDATE_RETARGETING_CONDITIONS = 'UPDATE_RETARGETING_CONDITIONS';
export type UPDATE_RETARGETING_CONDITIONS = typeof UPDATE_RETARGETING_CONDITIONS;

export interface UpdateRetargetingConditionsAction {
    type: UPDATE_RETARGETING_CONDITIONS;
    condition: RetargetingConditionData;
}

export function updateRetargetingConditions(
    condition: RetargetingConditionData
): UpdateRetargetingConditionsAction {
    return {
        type: UPDATE_RETARGETING_CONDITIONS,
        condition
    };
}

export const RESET_RETARGETING_ERRORS = 'RESET_RETARGETING_ERRORS';
export type RESET_RETARGETING_ERRORS = typeof RESET_RETARGETING_ERRORS;

export interface ResetRetargetingErrorsAction {
    type: RESET_RETARGETING_ERRORS;
    groupId: GroupId;
}

export function resetRetargetingErrors(
    groupId: GroupId
): ResetRetargetingErrorsAction {
    return {
        type: RESET_RETARGETING_ERRORS,
        groupId
    };
}

export const UPDATE_PRODUCTS_LIST = 'UPDATE_PRODUCTS_LIST';
export type UPDATE_PRODUCTS_LIST = typeof UPDATE_PRODUCTS_LIST;

export interface UpdateProductsListAction {
    type: UPDATE_PRODUCTS_LIST;
    productsList: ProductsList;
}

export function updateProductsList(
    productsList: ProductsList
): UpdateProductsListAction {
    return {
        type: UPDATE_PRODUCTS_LIST,
        productsList
    };
}

export const SAVE_GROUP_RATE_CORRECTIONS_EDITOR =
    'SAVE_GROUP_RATE_CORRECTIONS_EDITOR';
export type SAVE_GROUP_RATE_CORRECTIONS_EDITOR = typeof SAVE_GROUP_RATE_CORRECTIONS_EDITOR;

export interface SaveGroupRateCorrectionsEditorAction {
    type: SAVE_GROUP_RATE_CORRECTIONS_EDITOR;
    id: string;
}

export function saveGroupRateCorrectionsEditor(
    id: string
): SaveGroupRateCorrectionsEditorAction {
    return {
        type: SAVE_GROUP_RATE_CORRECTIONS_EDITOR,
        id
    };
}

export const CLOSE_GROUP_EDIT_RECOMMENDATION =
    'CLOSE_GROUP_EDIT_RECOMMENDATION';
export type CLOSE_GROUP_EDIT_RECOMMENDATION = typeof CLOSE_GROUP_EDIT_RECOMMENDATION;

export interface CloseGroupEditRecommendationAction {
    type: CLOSE_GROUP_EDIT_RECOMMENDATION;
    groupId: GroupId;
}

export function closeGroupEditRecommendation(
    groupId: GroupId
): CloseGroupEditRecommendationAction {
    return {
        type: CLOSE_GROUP_EDIT_RECOMMENDATION,
        groupId
    };
}

export type GroupsMultiEditActions =
    | GetCpcGroupsForEditAction
    | GetCpmGroupsForEditAction
    | UpdateShowConditionTypeAction
    | UpdateProductRestrictionIdAction
    | UpdateProductsListAction
    | GetRetargetingConditionsForGroupsEditAction
    | GetCampaignBasicDataAction
    | SaveGroupsAction
    | UpdateDataForEditGroupsAction
    | UpdateRetargetingConditionsForEditGroupsAction
    | ChangeGroupName
    | ShowGroupEditErrorAction
    | ShowGroupsClientValidationErrors
    | UpdateGroupAudienceTargetingInterests
    | UpdateGroupAudienceCryptaSocialDemo
    | UpdateGroupOutdoorMap
    | UpdateGroupIndoorMap
    | UpdateGroupAudienceCryptaGroupFamily
    | UpdateGroupAudienceCryptaAudioGenres
    | UpdateGroupAudienceCryptaBehavioral
    | UpdateGroupMinusPhrases
    | ShowGroupsPartialSaveResultAction
    | ChangeGroupSavedStatus
    | LeaveGroupsEdit
    | InputChangeGroupEditorMinusKeywords
    | ChangeGeneralRate
    | ChangeGroupEditorKeywords
    | DoneGroupEditorKeywords
    | OpenGroupEditorKeywords
    | OpenPhrasesSelectorWizardForGroupsEdit
    | ChangeInputGroupEditorKeywords
    | ApplySelectorWizardForGroupEdit
    | AudienceTargetingTemplateNameChange
    | CancelGroupEditorKeywords
    | UpdateGroupRateCorrectionsAction
    | ToggleGroupAutotargeting
    | ChangeGroupHrefParamsAction
    | ChangeGroupBannerHeadersFeedFieldAction
    | ChangeGroupBannerTextFeedFieldAction
    | OpenGroupFiltersListEditorAction
    | CloseGroupFiltersListEditorAction
    | SetGroupFiltersAction
    | GetGroupFiltersAction
    | DeleteGroupFilterAction
    | OpenGroupRetargetingEditor
    | SaveGroupRetargetingEditor
    | ChangeGroupRetargeting
    | CancelGroupEditorRegions
    | SaveGroupEditorRegions
    | ShowGroupEditorRegionsErrors
    | ClearGroupAudienceRetargetingCondition
    | ClearErrorsGroupEditorRegions
    | GetCampaignBasicDataAction
    | UpdateFiltersEditorAuxiliaryFilterAction
    | CreateSmartAdGroupAction
    | MoveNewGroupToCreatedAction
    | DeleteRetargetingConditionFromGroup
    | UpdateGroupEditorRegions
    | CreateTextAdGroupAction
    | UpdateRetargetingConditionsAction
    | ResetRetargetingErrorsAction
    | ChangeGroupEditPageStatusAction
    | UpdateGroupAudienceRetargetingCondition
    | SaveGroupMinusPhrasesEditorAction
    | SaveGroupNameEditorChangesAction
    | SaveGroupRateCorrectionsEditorAction
    | ChangeAudienceValueAction
    | UpdateGroupTypeAction
    | ResetGroupsEdit
    | CloseGroupEditRecommendationAction
    | GetInternalGroupsForEditAction;
